import { useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CreateTranslateDialog } from "@/components/case/create-translate-dialog";
import { usePhoneJsonEditorSessionOptional } from "@/components/phone-json-editor/phone-json-editor-session";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Field, FieldLabel } from "@/components/ui/field";
import { api, type CaseDetail, type CaseVersion } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { caseTitle } from "@/hooks/use-case-detail";
import { statusBadgeVariant, versionLabel } from "@/lib/case-format";
import { cn } from "@/lib/utils";

const selectClassName = cn(
  "h-9 w-full rounded-md border border-input bg-input/20 px-2.5 text-sm outline-none",
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
  "disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
);

function MetaSep() {
  return (
    <span aria-hidden className="select-none text-border">
      ·
    </span>
  );
}

export type VersionTab =
  | "meta"
  | "pipeline"
  | "preview"
  | "publish"
  | "agent"
  | "assets";

function detectVersionTab(pathname: string): VersionTab {
  if (pathname.endsWith("/meta")) return "meta";
  if (pathname.endsWith("/preview")) return "preview";
  if (pathname.endsWith("/publish")) return "publish";
  if (pathname.endsWith("/agent")) return "agent";
  if (pathname.endsWith("/assets")) return "assets";
  return "pipeline";
}

type CaseWorkspaceHeaderProps = {
  caseId: string;
  detail: CaseDetail;
  version?: CaseVersion | null;
  liveStatus?: string | null;
};

export function CaseWorkspaceHeader({
  caseId,
  detail,
  version = null,
  liveStatus = null,
}: CaseWorkspaceHeaderProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [translateOpen, setTranslateOpen] = useState(false);
  const editor = usePhoneJsonEditorSessionOptional();
  const title = caseTitle(detail);
  const casesQuery = useQuery({
    queryKey: ["cases"],
    queryFn: () => api.listCases(),
    enabled: auth.status === "authed",
  });
  const cases = casesQuery.data?.cases ?? [];
  const caseItems = cases.map((c) => c.id);
  const versions = detail.versions;
  const versionItems = versions.map((v) => v.id);
  const canCreateTranslation = version?.locale === "en";

  const onCaseChange = (nextCaseId: string | null) => {
    if (!nextCaseId || nextCaseId === caseId) return;
    if (pathname.includes("/source") || pathname.includes("/upload")) {
      void navigate({
        to: "/cases/$caseId/source",
        params: { caseId: nextCaseId },
      });
      return;
    }
    if (pathname.includes("/settings")) {
      void navigate({
        to: "/cases/$caseId/settings",
        params: { caseId: nextCaseId },
      });
      return;
    }
    void navigate({
      to: "/cases/$caseId/versions",
      params: { caseId: nextCaseId },
    });
  };

  const onVersionChange = (nextVersionId: string | null) => {
    if (!nextVersionId || !version || nextVersionId === version.id) return;
    const tab = detectVersionTab(pathname);
    void navigate({
      to: `/cases/$caseId/versions/$versionId/${tab}`,
      params: { caseId, versionId: nextVersionId },
    });
  };

  return (
    <div className="flex flex-col">
      <Breadcrumb>
        <BreadcrumbList className="gap-1 text-[11px] leading-normal text-muted-foreground/80">
          <BreadcrumbItem>
            <BreadcrumbLink render={<Link to="/" />}>案件</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="[&>svg]:size-3" />
          <BreadcrumbItem>
            {version ? (
              <BreadcrumbLink
                render={
                  <Link
                    to="/cases/$caseId/versions"
                    params={{ caseId }}
                  />
                }
              >
                {title}
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage className="text-muted-foreground">
                {title}
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
          {version ? (
            <>
              <BreadcrumbSeparator className="[&>svg]:size-3" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-muted-foreground">
                  {version.locale} v{version.versionN}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">
            {title}
          </h1>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono text-[11px]">{detail.case.id}</span>
            <MetaSep />
            <span>{detail.case.timezone}</span>
            {version ? (
              <>
                <MetaSep />
                <span>
                  {version.locale} v{version.versionN}
                </span>
                <MetaSep />
                <Badge
                  variant={statusBadgeVariant(version.status)}
                  className="h-5 px-1.5 text-[10px]"
                >
                  {version.status}
                </Badge>
              </>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {liveStatus != null ? (
            <Badge
              variant={
                liveStatus === "open"
                  ? "success"
                  : liveStatus === "error"
                    ? "destructive"
                    : liveStatus === "connecting"
                      ? "warning"
                      : "outline"
              }
              className="h-5 px-1.5 text-[10px]"
            >
              ws: {liveStatus}
            </Badge>
          ) : null}
          <Badge
            variant={statusBadgeVariant(detail.case.status)}
            className="h-5 px-1.5 text-[10px]"
          >
            {detail.case.status}
          </Badge>
          {editor && version ? (
            <Button
              type="button"
              size="sm"
              variant={editor.open ? "secondary" : "outline"}
              className="h-7 px-2.5"
              aria-pressed={editor.open}
              onClick={() => editor.setOpen(!editor.open)}
            >
              表单编辑器
              {editor.dirty ? (
                <span className="ml-1 inline-block size-1.5 rounded-full bg-amber-500" />
              ) : null}
            </Button>
          ) : null}
          {canCreateTranslation && version ? (
            <Button
              type="button"
              size="sm"
              className="h-7 px-2.5"
              onClick={() => setTranslateOpen(true)}
            >
              创建翻译
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <Field className="w-[13rem] max-w-full shrink-0 gap-1">
          <FieldLabel className="text-[11px] text-muted-foreground">
            案件
          </FieldLabel>
          <Combobox
            items={caseItems}
            value={caseId}
            itemToStringLabel={(id) => {
              const item = cases.find((c) => c.id === id);
              return item?.title || id;
            }}
            onValueChange={(next) => {
              if (typeof next === "string") onCaseChange(next);
            }}
          >
            <ComboboxInput
              className={selectClassName}
              showClear={false}
              placeholder="切换案件"
            />
            <ComboboxContent>
              <ComboboxEmpty>暂无案件</ComboboxEmpty>
              <ComboboxList>
                {(id) => {
                  const item = cases.find((c) => c.id === id);
                  return (
                    <ComboboxItem key={id} value={id}>
                      {item?.title || id}
                    </ComboboxItem>
                  );
                }}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Field>

        {version ? (
          <Field className="min-w-[16rem] max-w-md flex-1 gap-1">
            <FieldLabel className="text-[11px] text-muted-foreground">
              版本
            </FieldLabel>
            <Combobox
              items={versionItems}
              value={version.id}
              itemToStringLabel={(id) => {
                const item = versions.find((v) => v.id === id);
                return item ? versionLabel(item) : id;
              }}
              onValueChange={(next) => {
                if (typeof next === "string") onVersionChange(next);
              }}
            >
              <ComboboxInput
                className={selectClassName}
                showClear={false}
                placeholder="切换版本"
              />
              <ComboboxContent>
                <ComboboxEmpty>暂无版本</ComboboxEmpty>
                <ComboboxList>
                  {(id) => {
                    const item = versions.find((v) => v.id === id);
                    return (
                      <ComboboxItem key={id} value={id}>
                        {item ? versionLabel(item) : id}
                      </ComboboxItem>
                    );
                  }}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
        ) : null}
      </div>

      {canCreateTranslation && version ? (
        <CreateTranslateDialog
          open={translateOpen}
          onOpenChange={setTranslateOpen}
          caseId={caseId}
          versionId={version.id}
        />
      ) : null}
    </div>
  );
}
